import type { ClientEvent } from './queue';
import { computeCost } from './pricing';

interface WrapperDeps { enqueue: (e: ClientEvent) => void }

function nowSec() { return Math.floor(Date.now() / 1000); }

function emit(deps: WrapperDeps, provider: string, model: string,
              inputTokens: number, outputTokens: number) {
  deps.enqueue({
    type: 'api_call',
    occurredAt: nowSec(),
    provider, model,
    inputTokens, outputTokens,
    costUsd: computeCost(model, inputTokens, outputTokens),
  });
}

// Generic intercept helper: calls original method, reads usage from response, emits.
function intercept<T>(
  original: (...args: any[]) => Promise<T>,
  deps: WrapperDeps,
  provider: string,
  readModel: (args: any[]) => string,
  readUsage: (res: T) => { input: number; output: number } | null,
): (...args: any[]) => Promise<T> {
  return async (...args: any[]) => {
    const res = await original(...args);
    const u = readUsage(res);
    if (u) emit(deps, provider, readModel(args), u.input, u.output);
    return res;
  };
}

export function wrapGemini<T extends { models: { generateContent: any } }>(
  client: T, deps: WrapperDeps,
): T {
  const orig = client.models.generateContent.bind(client.models);
  client.models.generateContent = intercept(
    orig, deps, 'gemini',
    (args) => args[0]?.model ?? 'unknown',
    (res: any) => res?.usageMetadata
      ? { input: res.usageMetadata.promptTokenCount ?? 0,
          output: res.usageMetadata.candidatesTokenCount ?? 0 }
      : null,
  );
  return client;
}

export function wrapOpenAI<T extends { chat: { completions: { create: any } } }>(
  client: T, deps: WrapperDeps,
): T {
  const orig = client.chat.completions.create.bind(client.chat.completions);
  client.chat.completions.create = intercept(
    orig, deps, 'openai',
    (args) => args[0]?.model ?? 'unknown',
    (res: any) => res?.usage
      ? { input: res.usage.prompt_tokens ?? 0, output: res.usage.completion_tokens ?? 0 }
      : null,
  );
  return client;
}

export function wrapAnthropic<T extends { messages: { create: any } }>(
  client: T, deps: WrapperDeps,
): T {
  const orig = client.messages.create.bind(client.messages);
  client.messages.create = intercept(
    orig, deps, 'anthropic',
    (args) => args[0]?.model ?? 'unknown',
    (res: any) => res?.usage
      ? { input: res.usage.input_tokens ?? 0, output: res.usage.output_tokens ?? 0 }
      : null,
  );
  return client;
}
